import React, { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Trash2, UploadCloud } from "lucide-react";
import { AuthContext } from "../../../context/authContext";
import { updateShipment, uploadShipmentDoc } from "../../../reducers/shipment.slice";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectOption } from "../../../components/ui/select";
import PageHeader from "../../../components/shared/PageHeader";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const uploadBoxBase = {
  minHeight: 200,
  border: "1px dashed rgba(255,255,255,0.45)",
  borderRadius: 8,
  margin: "22px 0 16px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  transition: "background 0.15s, border-color 0.15s",
};

const shipping_address = [
  "23831 Via Fabricante, Suite 302, Mission Viejo, CA 92691",
  "RSC, 201 Carson Drive, Bear, DE 19701",
];

function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 12 }}>
      <Label style={{ color: "#8b9296", fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>{label}</Label>
      {children}
    </div>
  );
}

const UploadShipmentLabelPage = ({ dialogMode = false, shipmentId: shipmentIdProp, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const shipmentID = shipmentIdProp || location?.state?.shipmentId;
  const [showLoader, setShowLoader] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [formData, setFormData] = useState({
    tracking_number: "",
    ship_to_address: "",
    carrier_name: "",
  });
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleFileUpload = (files) => {
    setUploadedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileUpload(files);
      e.target.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleUploadShipmentReceipt = async () => {
    setShowLoader(true);

    if (uploadedFiles.length === 0) {
      const updateShipmentPayload = {
        id: shipmentID,
        tracking_number: [formData.tracking_number],
        status: "PENDING_SHIPMENT",
        shipment_type: "VENDOR",
        ship_to_address: formData.ship_to_address,
      };

      await dispatch(updateShipment(updateShipmentPayload)).then((resp) => {
        if (resp?.error && Object.keys(resp?.error)?.length > 0) {
          setShowLoader(false);
          showToast(resp?.error?.message, "error");
          return;
        }
        setShowLoader(false);
        if (dialogMode && onSuccess) onSuccess();
        else navigate(-1);
        return;
      });
      return;
    }

    const docFormData = new FormData();

    uploadedFiles.forEach((file) => {
      docFormData.append("files", file);
    });
    const payload = {
      itemId: shipmentID,
      docType: "LABEL",
    };

    dispatch(uploadShipmentDoc({ payload, docFormData })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const updateShipmentPayload = {
        id: shipmentID,
        tracking_number: [formData.tracking_number],
        status: "PENDING_SHIPMENT",
        shipment_type: "VENDOR",
        ship_to_address: formData.ship_to_address,
      };

      dispatch(updateShipment(updateShipmentPayload)).then((resp) => {
        if (resp?.error && Object.keys(resp?.error)?.length > 0) {
          setShowLoader(false);
          showToast(resp?.error?.message, "error");
          return;
        }
        setShowLoader(false);
        if (dialogMode && onSuccess) onSuccess();
        else navigate(-1);
      });
    });
  };

  const handleAddressSelect = (event) => {
    setFormData((prevData) => ({
      ...prevData,
      ship_to_address: event.target.value || "",
    }));
  };

  const handleTrackingKeyDown = (e) => {
    if (
      !/^[a-zA-Z0-9]$/.test(e.key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End"].includes(e.key) &&
      !e.ctrlKey &&
      !e.metaKey
    ) {
      e.preventDefault();
    }
  };

  const handleTrackingPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const alphanumericText = pastedText.replace(/[^a-zA-Z0-9]/g, "");
    setFormData({
      ...formData,
      tracking_number: formData.tracking_number + alphanumericText,
    });
  };

  return (
    <div style={{ width: "100%", display: "flex", justifyContent: "center", padding: dialogMode ? 0 : isMobile ? "8px 0 32px" : "24px 0 48px" }}>
      {showLoader && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.35)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          Loading...
        </div>
      )}

      <div style={{ width: "100%", maxWidth: 560 }}>
        {!dialogMode && (
          <>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "#c3c8cc",
                background: "transparent",
                border: "none",
                padding: 0,
                marginBottom: 18,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              <ArrowLeft size={16} /> Back
            </button>

            <PageHeader title="Upload Shipment Label" subtitle={`Upload the shipment label for the shipment ID: ${shipmentID}`} />
          </>
        )}

        <Field label="Tracking Number">
          <Input
            name="tracking_number"
            type="text"
            value={formData.tracking_number}
            onKeyDown={handleTrackingKeyDown}
            onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
            onPaste={handleTrackingPaste}
            placeholder="Enter Tracking Number"
            onWheel={(event) => event.target.blur()}
          />
        </Field>
        <Field label="Ship to Address">
          <Select value={formData.ship_to_address} onChange={handleAddressSelect} aria-label="Ship to Address">
            <SelectOption value="">Select the Address</SelectOption>
            {shipping_address.map((address) => (
              <SelectOption key={address} value={address}>
                RSC - {address}
              </SelectOption>
            ))}
          </Select>
        </Field>

        <button
          type="button"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById("file-input").click()}
          style={{
            ...uploadBoxBase,
            width: "100%",
            background: dragOver ? "rgba(0,173,181,0.08)" : "rgba(255,255,255,0.025)",
            borderColor: dragOver ? "#00ADB5" : "rgba(255,255,255,0.28)",
          }}
        >
          <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <UploadCloud size={42} color="#00ADB5" />
            <span style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>Click here or Drop a file to upload label</span>
            <span style={{ color: "#8b9296", fontSize: 12 }}>(Accepted File Types: .jpg, .png, .pdf)</span>
          </span>
          <input id="file-input" type="file" accept=".jpg,.png,.pdf" style={{ display: "none" }} onChange={handleFileInputChange} multiple />
        </button>

        <div style={{ marginTop: 18 }}>
          <div style={{ color: "#fff", fontSize: 15, fontWeight: 800, marginBottom: 10 }}>Uploaded Files</div>
          {uploadedFiles.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {uploadedFiles.map((file, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    border: "1px solid rgba(255,255,255,0.08)",
                    background: "rgba(255,255,255,0.025)",
                    padding: 12,
                    borderRadius: 8,
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, color: "#c3c8cc", fontSize: 13 }}>
                    <FileText size={15} style={{ flexShrink: 0 }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                  </span>
                  <Button type="button" variant="outline" size="sm" onClick={() => handleRemoveFile(index)} style={{ color: "#f27059", flexShrink: 0 }}>
                    <Trash2 size={14} /> Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: "#8b9296", fontSize: 13 }}>No files uploaded</div>
          )}
        </div>

        <Button
          type="button"
          onClick={handleUploadShipmentReceipt}
          disabled={!formData.tracking_number || !formData.ship_to_address}
          style={{ width: "100%", marginTop: 22 }}
        >
          Submit
        </Button>
        {dialogMode && (
          <Button type="button" variant="outline" onClick={onCancel} style={{ width: "100%", marginTop: 10 }}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
};

export default UploadShipmentLabelPage;
